import { useDashboardStatsContext } from "api/dashboard/dashboard-stats/DashboardStatsProvider";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useBranchContext } from "api/settings/branch/BranchProviders";
import { useRegionContext } from "api/settings/region/RegionProvider";
import { useUserContext } from "api/user/UserProvider";
import BarChartDashboard from "components/dashboard-bar-chart/BarChartDashboard";
import GlobalFilterBar from "components/dashboard-filter/GlobalFilterBar";
import MissionFilterBar from "components/dashboard-filter/MissionFilterBar";
import DoughnutChart from "components/doughnut-chart/DoughnutChart";
import { phoneSize } from "utils/screenSizes";
import CaFaeTable from "components/dashboard-ca-fae-table/CaFaeTable";
import moment from "moment";
import InfoContext from "pages/InfoContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate } from "utils/formatters";
import { statusHandler } from "utils/helper";
import {
  DASHBOARD_SHARED_SECTIONS,
  EXPORT_FILE_TYPE,
  FILTER_KEYS,
  QUERY_STATUS,
} from "constants/constants";
import {
  STATS_DATA,
  STATS_INFO_TITLE,
  STATS_GETTERS_MAPPING,
} from "utils/dashboardHelper";
import { OBJECTIVES_AND_REALIZED_FORM_DEFAULT_VALUES } from "constants/forms";
import { useForm } from "react-hook-form";
import ObjectivesAndRealized from "components/dashboard-objectives-and-realized/ObjectivesAndRealized";
import { usePersonContext } from "api/directory/person/PersonProvider";
import InfoPopup from "components/info-popup/InfoPopup";
import { useLocalStorage } from "react-use";
import { canUserPerformAction } from "access-roles/general-functions";
import ICDashboard from "components/dashboard-intercontracts/ICDashboard";
import { useNationalitiesContext } from "api/settings/nationalities/NationalitiesProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useDashboardContext } from "api/dashboard/DashboardProvider";
import { useObjectivesContext } from "api/datatrack/objectives/ObjectivesProvider";
import DocumentDownloadModal from "components/modal/DocumentDownloadModal";

const Dashboard = () => {
  const {
    dashboardIcStatsGetter,
    dashboardEdStatsGetter,
    dashboardClStatsGetter,
    dashboardMissionStatsGetter,
    dashboardBillingStatsGetter,
    dashboardFaeStatsGetter,
    dashboardRatioStatsGetter,
    dashboardStats,
    loadDataStatus,
  } = useDashboardStatsContext();

  const {
    getDashboardBarChart,
    dashboardBarChart,
    barChartStatus,
    getDashboardIC,
    icDashboard,
    getDashboardExportFile,
    icStatus,
    loadExportStatus,
    setLoadExportStatus,
  } = useDashboardContext();

  const { nationalitiesListGetter, nationalitiesList } =
    useNationalitiesContext();

  const {
    getPersonsByProfile,
    personsByProfileList,
    getPersonById,
    personById,
  } = usePersonContext();

  const { getClientsByName, clientsByName } = useClientContext();
  const { branchList, branchListGetter } = useBranchContext();
  const { regionList, regionListGetter } = useRegionContext();
  const {
    lang,
    userScope,
    connectedUser,
    mascaradeUser,
    userFilterStorage,
    postUserFilters,
    getUserFilters,
    userRoleList,
  } = useUserContext();

  const {
    loadObjectivesAndRealizedExportStatus,
    setLoadObjectivesAndRealizedExportStatus,
    getObjectivesAndRealizedExportFile,
  } = useObjectivesContext();

  const [currentUser, setCurrentUser] = useState(connectedUser);
  const [initiliazed, setInitiliazed] = useState(false);

  const currentDate = moment().format("YYYY-MM-DD");
  const currentYear = parseInt(moment().format("YYYY"));

  const [caFaeSelectedDate, setCaFaeSelectedDate] = useState(currentDate);
  const [caFaeSelectedYear, setCaFaeSelectedYear] = useState(currentYear);
  const [objReaSelectedYear, setObjReaSelectedYear] = useState(currentYear);
  const [objReaSelectedDate, setObjReaSelectedDate] = useState(currentDate);
  const [selectedDate, setSelectedDate] = useState("");
  const [popupOpened, setPopupOpened] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [code, setCode] = useState();
  const { jobList, jobListGetter } = useJobContext();

  const [storedDate, setStoredDate] = useLocalStorage(
    "dashboard_date_filter",
    ""
  );
  const [storedClients, setStoredClients] = useLocalStorage(
    "dashboard_clients_filter",
    []
  );
  const [storedSignedBy, setStoredSignedBy] = useLocalStorage(
    "dashboard_signed_by_filter",
    []
  );
  const [storedChargedBy, setStoredChargedBy] = useLocalStorage(
    "dashboard_charged_by_filter",
    []
  );
  const [storedMissionType, setStoredMissionType] = useLocalStorage(
    "dashboard_mission_type_filter",
    []
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: {
      ...OBJECTIVES_AND_REALIZED_FORM_DEFAULT_VALUES,
      date_filter: "",
      clients_filter: storedClients,
      signed_by_filter: storedSignedBy,
      charged_by_filter: storedChargedBy,
      mission_type_filter: storedMissionType,
    },
  });
  const dateFilter = watch("date_filter", "");
  const clientsFilter = watch("clients_filter", []);
  const signedByFilter = watch("signed_by_filter", []);
  const chargedByFilter = watch("charged_by_filter", []);
  const missionTypeByFilter = watch("mission_type_filter", []);
  const date = selectedDate || currentDate;

  const statsGetter = {
    dashboardIcStatsGetter,
    dashboardEdStatsGetter,
    dashboardClStatsGetter,
    dashboardMissionStatsGetter,
    dashboardBillingStatsGetter,
    dashboardFaeStatsGetter,
    dashboardRatioStatsGetter,
  };

  useEffect(() => {
    branchListGetter();
  }, [branchListGetter]);

  useEffect(() => {
    regionListGetter();
  }, [regionListGetter]);

  useEffect(() => {
    currentUser?.id && getPersonById(currentUser?.id);
  }, [userScope, userRoleList]);

  const getOptions = (search, profile = "") => {
    getPersonsByProfile({ search, profile });
  };

  useEffect(() => {
    getUserFilters(FILTER_KEYS.DASHBOARD);
    nationalitiesListGetter();
    jobListGetter();
    mascaradeUser && setCurrentUser(mascaradeUser);
  }, []);

  useEffect(() => {
    const dbDate = userFilterStorage?.date_filter;
    const storageDate = storedDate;

    if (userFilterStorage) {
      if (storageDate) {
        setSelectedDate(storageDate);
        setValue("date_filter", storageDate);
        setCaFaeSelectedDate(storageDate);
      } else if (dbDate) {
        setSelectedDate(dbDate);
        setStoredDate(dbDate);
        setValue("date_filter", dbDate);
        setCaFaeSelectedDate(dbDate);
      } else {
        setCaFaeSelectedDate(currentDate);
        setSelectedDate(currentDate);
        setValue("date_filter", currentDate);
      }
    }

    setValue("clients_filter", storedClients);
    setValue("signed_by_filter", storedSignedBy);
    setValue("charged_by_filter", storedChargedBy);
    setValue("mission_type_filter", storedMissionType);
    setInitiliazed(true);
  }, [userFilterStorage, storedDate, currentDate, setValue]);

  useEffect(() => {
    const closePopup = (e) => {
      if (popupOpened && !e.target.closest(".popup-information")) {
        setPopupOpened(false);
      }
    };
    document.addEventListener("click", closePopup);
    return () => document.removeEventListener("click", closePopup);
  }, [popupOpened]);

  const dashboardFilterOptions = useMemo(
    () => [
      ...branchList.map((b) => ({ value: `b-${b.id}`, label: b.name })),
      ...regionList.map((r) => ({ value: `r-${r.id}`, label: r.name })),
    ],
    [branchList, regionList]
  );

  const expandFilterIds = useCallback(
    (selected) => {
      if (!selected || selected.length === 0) return [];
      const branchIds = new Set();
      selected.forEach((item) => {
        const v = item.value;
        if (typeof v === "number") {
          branchIds.add(v);
          return;
        }
        if (typeof v !== "string") return;
        const dashIdx = v.indexOf("-");
        const type = dashIdx > 0 ? v.slice(0, dashIdx) : "";
        const rawId = dashIdx > 0 ? Number(v.slice(dashIdx + 1)) : Number(v);
        if (Number.isNaN(rawId)) return;
        if (type === "b" || type === "") {
          branchIds.add(rawId);
          return;
        }
        if (type === "r") {
          const targetRegions = new Set([rawId]);
          let grew = true;
          let safety = 100;
          while (grew && safety > 0) {
            grew = false;
            safety -= 1;
            regionList.forEach((r) => {
              if (
                r.parent_id != null &&
                targetRegions.has(r.parent_id) &&
                !targetRegions.has(r.id)
              ) {
                targetRegions.add(r.id);
                grew = true;
              }
            });
          }
          branchList.forEach((b) => {
            if (b.region?.some((rid) => targetRegions.has(rid))) {
              branchIds.add(b.id);
            }
          });
        }
      });
      if (branchIds.size === 0 && selected.length > 0) {
        // Nothing matched. Callers must hold while filtersResolvable is false:
        // -1 keeps the failure loud, where an empty array would read as "no
        // filter" and silently return the whole dataset.
        return [-1];
      }
      return Array.from(branchIds);
    },
    [branchList, regionList]
  );

  const hasRegionSelection = useMemo(
    () =>
      [...signedByFilter, ...chargedByFilter].some(
        (item) => typeof item?.value === "string" && item.value.startsWith("r-")
      ),
    [signedByFilter, chargedByFilter]
  );

  // A restored region filter needs branchList and regionList to expand into
  // branches. Until both are loaded the selection cannot be resolved, and every
  // fetch below must hold: querying anyway would return unfiltered data that
  // looks filtered.
  const filtersResolvable =
    !hasRegionSelection || (branchList.length > 0 && regionList.length > 0);

  const getGoogleChartsData = (date) => {
    const params = {
      date: date || selectedDate,
      scope: userScope,
      signed_by: expandFilterIds(signedByFilter),
      charged_by: expandFilterIds(chargedByFilter),
      mission_type: missionTypeByFilter,
      client: clientsFilter.map((item) => item.value),
    };

    Object.entries(STATS_GETTERS_MAPPING).forEach(([role, getterName]) => {
      if (
        canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: role,
        })
      ) {
        const getter = statsGetter[getterName];
        getter && getter(params);
      }
    });
  };

  useEffect(() => {
    if (!filtersResolvable) {
      return;
    }
    dashboardBillingStatsGetter({
      date: caFaeSelectedDate,
      scope: userScope,
      signed_by: expandFilterIds(signedByFilter),
      charged_by: expandFilterIds(chargedByFilter),
      mission_type: missionTypeByFilter,
      client: clientsFilter.map((item) => item.value),
    });
  }, [caFaeSelectedDate, filtersResolvable]);

  // The typed date must win over selectedDate, which is only updated on the
  // next render: without this, a call made right after typing would go out
  // with the previous date.
  const searchWithFilters = (searchedDate) => {
    const appliedDate = searchedDate || date;
    setCaFaeSelectedYear(moment(appliedDate).format("YYYY"));
    setObjReaSelectedYear(moment(appliedDate).format("YYYY"));
    getGoogleChartsData(appliedDate);
    getDashboardIC({
      page: 1,
      date: appliedDate,
      signed_by: expandFilterIds(signedByFilter),
    });
    getDashboardBarChart({
      page: 1,
      date: appliedDate,
      signed_by: expandFilterIds(signedByFilter),
      charged_by: expandFilterIds(chargedByFilter),
      mission_type: missionTypeByFilter,
      client: clientsFilter.map((item) => item.value),
    });
  };

  // The mission filter bar only drives the CA/FAE table and the bar chart.
  // Refetching the doughnuts and the IC table would suggest they are affected,
  // while none of these filters ever reaches them.
  const searchMissionBlocks = (searchedDate) => {
    const appliedDate = searchedDate || date;
    dashboardBillingStatsGetter({
      date: caFaeSelectedDate,
      scope: userScope,
      signed_by: expandFilterIds(signedByFilter),
      charged_by: expandFilterIds(chargedByFilter),
      mission_type: missionTypeByFilter,
      client: clientsFilter.map((item) => item.value),
    });
    getDashboardBarChart({
      page: 1,
      date: appliedDate,
      signed_by: expandFilterIds(signedByFilter),
      charged_by: expandFilterIds(chargedByFilter),
      mission_type: missionTypeByFilter,
      client: clientsFilter.map((item) => item.value),
    });
  };

  const exportBarChart = () => {
    if (!filtersResolvable) {
      return;
    }
    getDashboardExportFile({
      queryParams: {
        date,
        signed_by: expandFilterIds(signedByFilter),
        charged_by: expandFilterIds(chargedByFilter),
        mission_type: missionTypeByFilter,
        client: clientsFilter.map((item) => item.value),
      },
      lang,
      exportFileType: EXPORT_FILE_TYPE.BAR_CART,
    });
  };

  const onSubmit = async () => {
    if (!dateFilter || dateFilter === "") {
      return;
    }

    const filtersToSave = {
      date_filter: dateFilter,
    };

    const stringifiedFilter = JSON.stringify(filtersToSave);
    await postUserFilters(
      {
        key: FILTER_KEYS.DASHBOARD,
        value: stringifiedFilter,
      },
      FILTER_KEYS.DASHBOARD
    );
    setStoredDate(dateFilter);
    setSelectedDate(dateFilter);
    searchWithFilters(dateFilter);
  };

  const onSubmitMissionFilters = () => {
    if (!dateFilter || dateFilter === "") {
      return;
    }
    searchMissionBlocks(dateFilter);
  };

  useEffect(() => {
    initiliazed && filtersResolvable && onSubmit();
  }, [userScope, initiliazed, filtersResolvable]);

  const values = useMemo(
    () => ({
      objReaSelectedDate,
      setObjReaSelectedDate,
      icStatus,
      caFaeSelectedDate,
      setCaFaeSelectedDate,
      getDashboardIC,
      icDashboard,
      getDashboardExportFile,
      getDashboardBarChart,
      dashboardBarChart,
      barChartStatus,
      jobList,
      nationalitiesList,
      refresh,
      setRefresh,
      personById,
      setCode,
      control,
      errors,
      handleSubmit,
      register,
      setValue,
      watch,
      clearErrors,
      branchList,
      dashboardFilterOptions,
      expandFilterIds,
      filtersResolvable,
      getOptions,
      personsByProfileList,
      dashboardStats,
      loadDataStatus,
      dateFilter,
      clientsFilter,
      getFilter: searchWithFilters,
      lang,
      getPersonsByProfile,
      clientsByName,
      userScope,
      clientsList: clientsByName,
      getClients: getClientsByName,
      caFaeSelectedYear,
      setCaFaeSelectedYear,
      objReaSelectedYear,
      setObjReaSelectedYear,
      selectedDate,
      setSelectedDate,
      signedByFilter,
      chargedByFilter,
      missionTypeByFilter,
      storedMissionType,
      setStoredChargedBy,
      setStoredSignedBy,
      setStoredClients,
      setStoredMissionType,
      storedDate,
      storedClients,
      storedSignedBy,
      storedChargedBy,
      setStoredDate,
      loadExportStatus,
      setLoadExportStatus,
      loadObjectivesAndRealizedExportStatus,
      setLoadObjectivesAndRealizedExportStatus,
      exportBarChart,
      getObjectivesAndRealizedExportFile,
    }),
    [
      objReaSelectedDate,
      icStatus,
      caFaeSelectedDate,
      icDashboard,
      dashboardBarChart,
      barChartStatus,
      jobList,
      nationalitiesList,
      signedByFilter,
      chargedByFilter,
      missionTypeByFilter,
      caFaeSelectedYear,
      objReaSelectedYear,
      selectedDate,
      refresh,
      personById,
      dashboardStats,
      loadDataStatus,
      dateFilter,
      clientsFilter,
      searchWithFilters,
      lang,
      clientsByName,
      branchList,
      dashboardFilterOptions,
      expandFilterIds,
      filtersResolvable,
      userScope,
      loadExportStatus,
      setLoadExportStatus,
      loadObjectivesAndRealizedExportStatus,
      setLoadObjectivesAndRealizedExportStatus,
      exportBarChart,
      getObjectivesAndRealizedExportFile,
    ]
  );

  return (
    <InfoContext.Provider value={values}>
      <DocumentDownloadModal
        status={statusHandler({
          statusList: [loadExportStatus, loadObjectivesAndRealizedExportStatus],
        })}
        onClose={() => {
          setLoadExportStatus(QUERY_STATUS.INITIAL);
          setLoadObjectivesAndRealizedExportStatus(QUERY_STATUS.INITIAL);
        }}
      />
      <GlobalFilterBar
        onSubmit={handleSubmit(onSubmit)}
        setPopupOpened={setPopupOpened}
        popupOpened={popupOpened}
        formatDate={formatDate}
      />
      <div className="donut-parent">
        <div
          className="donut-date-title"
          style={{
            display: "flex",
            flexDirection: phoneSize ? "column" : "row",
            marginBottom: "-5rem",
          }}
        >
          {popupOpened && (
            <InfoPopup
              stats={true}
              dataInfo={STATS_DATA}
              mainTitle={STATS_INFO_TITLE}
            />
          )}
        </div>
        <div className="doughnut-charts">
          <DoughnutChart
            lang={lang}
            userScope={userScope}
            stats={{
              missions: [
                { url: `/${userScope}/missions` },
                { statistics: dashboardStats?.missionStats },
              ],
              ic: [
                {
                  url: `/${userScope}/dashboard/shared/${DASHBOARD_SHARED_SECTIONS?.ic}`,
                },
                { statistics: dashboardStats?.icStats },
              ],
              ed: [
                {
                  url: `/${userScope}/dashboard/shared/${DASHBOARD_SHARED_SECTIONS?.ed}`,
                },
                { statistics: dashboardStats?.edStats },
              ],
              cl: [
                {
                  url: `/${userScope}/dashboard/shared/${DASHBOARD_SHARED_SECTIONS?.cl}`,
                },
                { statistics: dashboardStats?.clStats },
              ],
              ratio: [
                {
                  url: `/${userScope}/dashboard/shared/${DASHBOARD_SHARED_SECTIONS?.ic}`,
                },
                { statistics: dashboardStats?.ratioStats },
              ],
              fae: [
                { url: `/${userScope}/inputs/spreadsheet` },
                { statistics: dashboardStats?.faeStats },
              ],
            }}
            loadingStatus={loadDataStatus}
          />
        </div>
      </div>
      <ObjectivesAndRealized />
      <ICDashboard />
      <MissionFilterBar onSubmit={handleSubmit(onSubmitMissionFilters)} />
      <CaFaeTable />
      <BarChartDashboard />
    </InfoContext.Provider>
  );
};

export default Dashboard;
